import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useCustomerSelection } from '../context/CustomerSelectionContext'
import { partnerService } from '../services/partnerService'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  Building2,
  Edit3,
  ImagePlus,
  Mail,
  MapPin,
  PhoneIcon,
  Trash2,
} from 'lucide-react'
import { SECURITY_FEATURE_ENABLED } from '../config/featureFlags'
import { useCustomerFeatures } from '../hooks/useCustomerFeatures'
import { usePartnerFeature } from '../hooks/usePartnerFeature'
import { Customer } from '../types/partner'

import { RmmReconciliationCard } from '../components/customers/RmmReconciliationCard'
import { TenantsView } from './TenantsView'
import { UsersView } from './UsersView'
import { DevicesView } from './DevicesView'
import { SecurityView } from './SecurityView'
import { MarketplaceView } from './MarketplaceView'
import Telstra from './Telstra'
import { AuditView } from './AuditView'
import { RecurringTasksTab } from './RecurringTasksTab'

interface CustomerProfileFormState {
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  contactNumber: string
  country: string
  industry: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  notes: string
}

function createProfileForm(customer: Customer | null): CustomerProfileFormState {
  return {
    adminFirstName: customer?.adminFirstName || '',
    adminLastName: customer?.adminLastName || '',
    adminEmail: customer?.adminEmail || '',
    contactNumber: customer?.contactNumber || '',
    country: customer?.country || '',
    industry: customer?.industry || '',
    addressLine1: customer?.addressLine1 || '',
    addressLine2: customer?.addressLine2 || '',
    city: customer?.city || '',
    state: customer?.state || '',
    postalCode: customer?.postalCode || '',
    notes: customer?.notes || '',
  }
}

export default function CustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { managedCustomers, partner, updateManagedCustomer } = useAuth()
  const { setSelectedCustomer } = useCustomerSelection()
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const baseCustomer = React.useMemo(
    () => managedCustomers.find((managedCustomer) => managedCustomer.customerId === customerId) || null,
    [managedCustomers, customerId]
  )
  const { isFeatureEnabled } = useCustomerFeatures(customerId)
  const partnerRecurringTasksEnabled = usePartnerFeature('recurringTasks')
  // Recurring Tasks require BOTH the partner-level toggle (set in
  // aitryx-operations) AND the customer-level feature flag.
  const recurringTasksEnabled = partnerRecurringTasksEnabled && isFeatureEnabled('recurringTasks')

  const [customer, setCustomer] = React.useState<Customer | null>(baseCustomer)
  const [isCustomerLoading, setIsCustomerLoading] = React.useState(!baseCustomer)
  const [customerError, setCustomerError] = React.useState<string | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)
  const [isSavingProfile, setIsSavingProfile] = React.useState(false)
  const [isSavingLogo, setIsSavingLogo] = React.useState(false)
  const [profileForm, setProfileForm] = React.useState<CustomerProfileFormState>(() => createProfileForm(baseCustomer))
  const [customerStats, setCustomerStats] = React.useState({
    totalUsers: 0,
    totalDevices: 0,
    totalLicenses: 0,
    isLoading: true,
  })

  const syncCustomerState = React.useCallback((updatedCustomer: Customer) => {
    setCustomer(updatedCustomer)
    setSelectedCustomer(updatedCustomer)
    updateManagedCustomer(updatedCustomer)
  }, [setSelectedCustomer, updateManagedCustomer])

  const baseCustomerRef = React.useRef(baseCustomer)
  baseCustomerRef.current = baseCustomer

  React.useEffect(() => {
    if (baseCustomer) {
      setCustomer((currentCustomer) => {
        if (!currentCustomer || currentCustomer.customerId !== baseCustomer.customerId) {
          return baseCustomer
        }

        return { ...baseCustomer, ...currentCustomer }
      })
    }
  }, [baseCustomer])

  const loadCustomerDetails = React.useCallback(async (showLoading: boolean = true) => {
    const base = baseCustomerRef.current

    if (!customerId) {
      setCustomer(null)
      setIsCustomerLoading(false)
      return
    }

    if (!partner?.partnerId) {
      setCustomer(base)
      setIsCustomerLoading(false)
      return
    }

    if (showLoading) {
      setIsCustomerLoading(true)
    }

    try {
      setCustomerError(null)
      const response = await partnerService.getCustomerDetails(partner.partnerId, customerId)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load customer details')
      }

      const refreshedCustomer = { ...base, ...response.data } as Customer
      syncCustomerState(refreshedCustomer)
    } catch (error) {
      setCustomerError(error instanceof Error ? error.message : 'Failed to load customer details')

      if (!base) {
        setCustomer(null)
      }
    } finally {
      setIsCustomerLoading(false)
    }
  }, [customerId, partner?.partnerId, syncCustomerState])

  React.useEffect(() => {
    void loadCustomerDetails()
  }, [loadCustomerDetails])

  React.useEffect(() => {
    if (!successMessage) {
      return
    }

    const timer = window.setTimeout(() => setSuccessMessage(null), 5000)
    return () => window.clearTimeout(timer)
  }, [successMessage])

  const statsRequestKeyRef = React.useRef<string | null>(null)
  const isStatsRequestInFlightRef = React.useRef(false)

  React.useEffect(() => {
    let isCancelled = false

    const currentCustomerId = customer?.customerId || customerId
    const currentPartnerId = partner?.partnerId
    const currentLicenseCount = customer?.licenseCount || 0

    if (!currentCustomerId || !currentPartnerId) {
      setCustomerStats((prev) => ({
        ...prev,
        totalLicenses: currentLicenseCount,
        isLoading: false,
      }))
      return
    }

    const requestKey = `${currentPartnerId}:${currentCustomerId}`
    if (isStatsRequestInFlightRef.current || statsRequestKeyRef.current === requestKey) {
      setCustomerStats((prev) => ({
        ...prev,
        totalLicenses: currentLicenseCount,
      }))
      return
    }

    const loadCustomerStats = async () => {
      isStatsRequestInFlightRef.current = true
      statsRequestKeyRef.current = requestKey

      setCustomerStats((prev) => ({
        ...prev,
        totalLicenses: currentLicenseCount,
        isLoading: true,
      }))

      try {
        const [usersResponse, devicesResponse] = await Promise.all([
          partnerService.getPartnerUsers(currentPartnerId, {
            customerId: currentCustomerId,
            page: 1,
            pageSize: 1,
          }),
          partnerService.getAllPartnerDevices(currentPartnerId, {
            customerId: currentCustomerId,
            page: 1,
            pageSize: 1,
          }),
        ])

        if (isCancelled) {
          return
        }

        const totalUsers = usersResponse.success
          ? (typeof usersResponse.totalCount === 'number' ? usersResponse.totalCount : usersResponse.data?.length || 0)
          : 0

        const totalDevices = devicesResponse.success
          ? (typeof devicesResponse.totalCount === 'number' ? devicesResponse.totalCount : devicesResponse.data?.length || 0)
          : 0

        setCustomerStats({
          totalUsers,
          totalDevices,
          totalLicenses: currentLicenseCount,
          isLoading: false,
        })
      } catch (error) {
        if (isCancelled) {
          return
        }

        setCustomerStats({
          totalUsers: 0,
          totalDevices: 0,
          totalLicenses: currentLicenseCount,
          isLoading: false,
        })
      } finally {
        isStatsRequestInFlightRef.current = false
      }
    }

    void loadCustomerStats()

    return () => {
      isCancelled = true
    }
  }, [customer?.customerId, customer?.licenseCount, customerId, partner?.partnerId])

  const openEditDialog = React.useCallback(() => {
    setProfileForm(createProfileForm(customer))
    setIsEditDialogOpen(true)
  }, [customer])

  const updateProfileField = React.useCallback((field: keyof CustomerProfileFormState, value: string) => {
    setProfileForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }))
  }, [])

  const saveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!partner?.partnerId || !customer) {
      return
    }

    try {
      setCustomerError(null)
      setIsSavingProfile(true)

      const response = await partnerService.updateCustomerContact(partner.partnerId, customer.customerId, profileForm)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update customer profile')
      }

      syncCustomerState({ ...customer, ...response.data })
      await loadCustomerDetails(false)

      setSuccessMessage('Customer profile updated successfully')
      setIsEditDialogOpen(false)
    } catch (error) {
      setCustomerError(error instanceof Error ? error.message : 'Failed to update customer profile')
    } finally {
      setIsSavingProfile(false)
    }
  }

  const triggerLogoPicker = React.useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleLogoFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !partner?.partnerId || !customer) {
      return
    }

    try {
      setCustomerError(null)
      setIsSavingLogo(true)

      const uploadResponse = await partnerService.uploadCustomerLogo(partner.partnerId, customer.customerId, file)
      if (!uploadResponse.success || !uploadResponse.data?.imageUrl) {
        throw new Error(uploadResponse.error || 'Failed to upload customer logo')
      }

      const saveResponse = await partnerService.saveCustomerLogo(
        partner.partnerId,
        customer.customerId,
        uploadResponse.data.imageUrl
      )
      if (!saveResponse.success) {
        throw new Error(saveResponse.error || 'Failed to save customer logo')
      }

      syncCustomerState({ ...customer, logoUrl: uploadResponse.data.imageUrl })
      await loadCustomerDetails(false)

      setSuccessMessage('Customer logo updated successfully')
    } catch (error) {
      setCustomerError(error instanceof Error ? error.message : 'Failed to upload customer logo')
    } finally {
      setIsSavingLogo(false)
      event.target.value = ''
    }
  }

  const removeLogo = async () => {
    if (!partner?.partnerId || !customer) {
      return
    }

    try {
      setCustomerError(null)
      setIsSavingLogo(true)

      const response = await partnerService.saveCustomerLogo(partner.partnerId, customer.customerId, null)
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove customer logo')
      }

      syncCustomerState({ ...customer, logoUrl: undefined })
      await loadCustomerDetails(false)

      setSuccessMessage('Customer logo removed successfully')
    } catch (error) {
      setCustomerError(error instanceof Error ? error.message : 'Failed to remove customer logo')
    } finally {
      setIsSavingLogo(false)
    }
  }

  if (isCustomerLoading && !customer) {
    return (
      <div className="flex-1 bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Loading customer profile...</p>
        </div>
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="flex-1 bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Customer not found</h3>
          <p className="text-sm text-slate-500 mb-6 max-w-sm">
            The customer you're looking for doesn't exist or you don't have access to it.
          </p>
          <Button onClick={() => navigate('/customers')}>
            Back to Customers
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        className="hidden"
        aria-label="Upload customer logo"
        onChange={handleLogoFileSelected}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-[1800px] mx-auto space-y-4">
          {customerError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {customerError}
            </div>
          )}

          {successMessage && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {successMessage}
            </div>
          )}

          <div className="flex gap-6">
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden sticky top-0">
                <div className="p-6 text-center border-b border-slate-200">
                  <div className="relative inline-block">
                    <div className="w-28 h-28 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center mx-auto">
                      {customer.logoUrl ? (
                        <img src={customer.logoUrl} alt={`${customer.customerName} logo`} className="h-full w-full object-cover" />
                      ) : (
                        <Building2 className="h-14 w-14 text-slate-400" />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={triggerLogoPicker}
                      className="absolute bottom-1 right-1 flex h-8 w-8 items-center justify-center rounded-full border border-white bg-slate-900 text-white shadow-sm transition hover:bg-slate-800"
                      aria-label={customer.logoUrl ? 'Change customer logo' : 'Upload customer logo'}
                    >
                      <ImagePlus className="h-4 w-4" />
                    </button>
                  </div>
                  <h2 className="mt-4 text-lg font-semibold text-slate-900">{customer.customerName}</h2>
                  <p className="text-sm text-slate-500 font-mono mt-1">{customer.customerId.substring(0, 24)}...</p>
                </div>

                <div className="p-6 border-b border-slate-200">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-slate-900">{customerStats.isLoading ? '—' : customerStats.totalUsers}</div>
                      <div className="text-xs text-slate-500 mt-1">Users</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">{customerStats.isLoading ? '—' : customerStats.totalDevices}</div>
                      <div className="text-xs text-slate-500 mt-1">Devices</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">{customerStats.totalLicenses}</div>
                      <div className="text-xs text-slate-500 mt-1">Licenses</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-slate-700">Location</div>
                      <div className="text-slate-500 mt-0.5">{customer.country || 'Not specified'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <PhoneIcon className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-slate-700">Phone</div>
                      <div className="text-slate-500 mt-0.5">{customer.contactNumber || 'Not provided'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 min-w-0">
                    <Mail className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm min-w-0">
                      <div className="font-medium text-slate-700">Email</div>
                      <div
                        className="text-slate-500 mt-0.5 break-words [overflow-wrap:anywhere]"
                        title={customer.adminEmail}
                      >
                        {customer.adminEmail}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 pt-0 space-y-2">
                  <Button fullWidth onClick={openEditDialog}>
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                  <Button variant="outline" fullWidth onClick={triggerLogoPicker} loading={isSavingLogo}>
                    <ImagePlus className="h-4 w-4 mr-2" />
                    {customer.logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    disabled={!customer.logoUrl || isSavingLogo}
                    onClick={removeLogo}
                    className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Logo
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <Tabs defaultValue="overview" className="space-y-6">
                <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-transparent p-0 h-auto">
                  <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                    Users
                  </TabsTrigger>
                  {isFeatureEnabled('devices') && (
                    <TabsTrigger value="devices" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                      Devices
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="activity" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                    Activity
                  </TabsTrigger>
                  {isFeatureEnabled('marketplace') && (
                    <TabsTrigger value="marketplace" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                      Marketplace
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="notifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                    Notifications
                  </TabsTrigger>
                  {isFeatureEnabled('telstra') && (
                    <TabsTrigger value="telstra" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                      Telstra
                    </TabsTrigger>
                  )}
                  {SECURITY_FEATURE_ENABLED && isFeatureEnabled('security') && (
                    <TabsTrigger value="security" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                      Security
                    </TabsTrigger>
                  )}
                  {recurringTasksEnabled && (
                    <TabsTrigger value="recurring-tasks" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent">
                      Recurring Tasks
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold text-slate-900">Profile About:</h3>
                      <button
                        type="button"
                        onClick={openEditDialog}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium uppercase tracking-wide"
                      >
                        Update
                      </button>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                      {customer.notes
                        ? customer.notes
                        : `${customer.customerName} is a valued customer with ${customer.licenseCount} licenses on the ${customer.subscriptionTier} tier.`}
                    </p>
                  </div>

                  {partner?.partnerId && customerId && (
                    <RmmReconciliationCard
                      partnerId={partner.partnerId}
                      customerId={customerId}
                      customerName={customer.customerName}
                    />
                  )}

                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-base font-semibold text-slate-900">Profile Details:</h3>
                      <button
                        type="button"
                        onClick={openEditDialog}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium uppercase tracking-wide"
                      >
                        Edit Profile
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Customer Name:</label>
                        <div className="mt-2 text-sm text-slate-900">{customer.customerName}</div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Subscription Tier:</label>
                        <div className="mt-2">
                          <Badge variant={customer.subscriptionTier === 'Enterprise' ? 'success' : 'default'}>
                            {customer.subscriptionTier}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Customer ID:</label>
                        <div className="mt-2 text-sm text-slate-900 font-mono">{customer.customerId}</div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">License Count:</label>
                        <div className="mt-2 text-sm text-slate-900">{customer.licenseCount}</div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Email Address:</label>
                        <div className="mt-2 text-sm text-slate-900">{customer.adminEmail}</div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Contact Number:</label>
                        <div className="mt-2 text-sm text-slate-900">{customer.contactNumber || 'Not provided'}</div>
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Location:</label>
                        <div className="mt-2 text-sm text-slate-900">{customer.country || 'Not specified'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <h3 className="text-base font-semibold text-slate-900 mb-4">Tenant Information</h3>
                    <TenantsView />
                  </div>
                </TabsContent>

                <TabsContent value="users">
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <UsersView />
                  </div>
                </TabsContent>

                {isFeatureEnabled('devices') && (
                  <TabsContent value="devices">
                    <div className="bg-white rounded-lg border border-slate-200 p-6">
                      <DevicesView />
                    </div>
                  </TabsContent>
                )}

                <TabsContent value="activity">
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <AuditView />
                  </div>
                </TabsContent>

                {isFeatureEnabled('marketplace') && (
                  <TabsContent value="marketplace">
                    <div className="bg-white rounded-lg border border-slate-200 p-6">
                      <MarketplaceView />
                    </div>
                  </TabsContent>
                )}

                <TabsContent value="notifications">
                  <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 mb-4">
                      <Mail className="h-6 w-6 text-slate-400" />
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 mb-2">Notification Settings Coming Soon</h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto">
                      We&apos;re still building this surface. You&apos;ll soon be able to configure email digests, alert
                      thresholds, and per-channel preferences for this customer.
                    </p>
                  </div>
                </TabsContent>

                {isFeatureEnabled('telstra') && (
                  <TabsContent value="telstra">
                    <div className="bg-white rounded-lg border border-slate-200 p-6">
                      <Telstra customer={customer} />
                    </div>
                  </TabsContent>
                )}

                {SECURITY_FEATURE_ENABLED && isFeatureEnabled('security') && (
                  <TabsContent value="security">
                    <div className="bg-white rounded-lg border border-slate-200 p-6">
                      <SecurityView />
                    </div>
                  </TabsContent>
                )}

                {recurringTasksEnabled && (
                  <TabsContent value="recurring-tasks">
                    <div className="bg-white rounded-lg border border-slate-200 p-6">
                      <RecurringTasksTab customerId={customer.customerId} customerName={customer.customerName} />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Customer Profile</DialogTitle>
            <DialogDescription>
              Update the partner-managed contact and address details for this customer.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={saveProfile} className="space-y-6">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Profile About</label>
              <Textarea
                rows={3}
                placeholder="Describe this customer organisation…"
                value={profileForm.notes}
                onChange={(event) => updateProfileField('notes', event.target.value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Admin first name</label>
                <Input value={profileForm.adminFirstName} onChange={(event) => updateProfileField('adminFirstName', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Admin last name</label>
                <Input value={profileForm.adminLastName} onChange={(event) => updateProfileField('adminLastName', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Admin email</label>
                <Input type="email" value={profileForm.adminEmail} onChange={(event) => updateProfileField('adminEmail', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Contact number</label>
                <Input value={profileForm.contactNumber} onChange={(event) => updateProfileField('contactNumber', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Industry</label>
                <Input value={profileForm.industry} onChange={(event) => updateProfileField('industry', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Country</label>
                <Input value={profileForm.country} onChange={(event) => updateProfileField('country', event.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Address line 1</label>
                <Input value={profileForm.addressLine1} onChange={(event) => updateProfileField('addressLine1', event.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Address line 2</label>
                <Input value={profileForm.addressLine2} onChange={(event) => updateProfileField('addressLine2', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">City</label>
                <Input value={profileForm.city} onChange={(event) => updateProfileField('city', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">State</label>
                <Input value={profileForm.state} onChange={(event) => updateProfileField('state', event.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Postal code</label>
                <Input value={profileForm.postalCode} onChange={(event) => updateProfileField('postalCode', event.target.value)} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={isSavingProfile}>
                Cancel
              </Button>
              <Button type="submit" loading={isSavingProfile}>
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
